(() => {
  const controllerKey = Symbol.for('blue-flamingo.building.controller');
  const existingController = window[controllerKey];

  if (existingController) {
    existingController.init(document);
    return;
  }

  const instances = new Map();
  const topicKeys = ['devices', 'experience', 'storage', 'ai', 'security', 'services'];

  const hashTopic = () => {
    let hash = window.location.hash;

    try {
      hash = decodeURIComponent(hash);
    } catch {
      return null;
    }

    return hash.toLowerCase() === '#bf-intelligence' ? 'ai' : null;
  };

  const restoreAttribute = (element, name, value) => {
    if (value === null) {
      element.removeAttribute(name);
    } else {
      element.setAttribute(name, value);
    }
  };

  const createInstance = (root) => {
    const topics = Array.from(root.querySelectorAll('button[data-building-topic]'));
    const panels = Array.from(root.querySelectorAll('[data-building-panel]'));
    const panelByTopic = new Map(panels.map((panel) => [panel.dataset.buildingPanel, panel]));
    const topicByKey = new Map(topics.map((topic) => [topic.dataset.buildingTopic, topic]));

    if (topics.length !== topicKeys.length || !topicKeys.every((key) => topicByKey.has(key) && panelByTopic.has(key))) {
      return null;
    }

    const rootAttributes = ['data-building-active', 'data-building-enhanced', 'data-building-state', 'data-building-motion'].map((name) => [name, root.getAttribute(name)]);
    const topicAttributes = topics.map((topic) => [topic, topic.getAttribute('aria-selected'), topic.getAttribute('tabindex')]);
    const panelVisibility = panels.map((panel) => [panel, panel.hidden]);
    const map = root.querySelector('[data-building-map]');
    let observer = null;
    let motionQuery = null;
    let motionStarted = false;
    let motionFinished = false;
    let disposed = false;
    let coreTimer = null;
    let linksTimer = null;
    let previousHashTopic = hashTopic();

    const clearMotion = () => {
      window.clearTimeout(coreTimer);
      window.clearTimeout(linksTimer);
      coreTimer = null;
      linksTimer = null;

      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };

    const finishMotion = () => {
      clearMotion();
      motionStarted = true;
      motionFinished = true;

      if (!disposed) {
        root.dataset.buildingState = 'ready';
        root.dataset.buildingMotion = 'complete';
      }
    };

    const selectTopic = (key, finish = false) => {
      if (!topicByKey.has(key)) {
        return;
      }

      if (finish) {
        finishMotion();
      }

      topics.forEach((topic) => {
        const selected = topic.dataset.buildingTopic === key;
        topic.setAttribute('aria-selected', String(selected));
        topic.tabIndex = selected ? 0 : -1;
      });

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.buildingPanel !== key;
      });

      root.dataset.buildingActive = key;
    };

    const eventTopic = (event) => {
      const target = event.target;
      const topic = target && typeof target.closest === 'function' ? target.closest('button[data-building-topic]') : null;
      return topics.includes(topic) ? topic : null;
    };

    const onClick = (event) => {
      const topic = eventTopic(event);

      if (topic) {
        selectTopic(topic.dataset.buildingTopic, true);
      }
    };

    const onKeyDown = (event) => {
      const topic = eventTopic(event);

      if (!topic || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      const index = topics.indexOf(topic);
      let nextIndex;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (index + 1) % topics.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (index - 1 + topics.length) % topics.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = topics.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      const nextTopic = topics[nextIndex];
      selectTopic(nextTopic.dataset.buildingTopic, true);
      nextTopic.focus({ preventScroll: true });
    };

    const onFocusIn = (event) => {
      if (eventTopic(event)) {
        finishMotion();
      }
    };

    const onHashChange = () => {
      const nextHashTopic = hashTopic();

      if (nextHashTopic || previousHashTopic) {
        selectTopic(nextHashTopic || 'experience', true);
      }

      previousHashTopic = nextHashTopic;
    };

    const onMotionChange = (event) => {
      if (event.matches) {
        finishMotion();
      }
    };

    const startMotion = () => {
      if (motionStarted || motionFinished || disposed) {
        return;
      }

      if (motionQuery && motionQuery.matches) {
        finishMotion();
        return;
      }

      motionStarted = true;

      if (observer) {
        observer.disconnect();
        observer = null;
      }

      root.dataset.buildingState = 'core';
      root.dataset.buildingMotion = 'running';
      coreTimer = window.setTimeout(() => {
        coreTimer = null;

        if (disposed || motionFinished) {
          return;
        }

        root.dataset.buildingState = 'links';
        linksTimer = window.setTimeout(finishMotion, 450);
      }, 600);
    };

    selectTopic(previousHashTopic || 'experience');
    root.dataset.buildingEnhanced = 'true';
    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeyDown);
    root.addEventListener('focusin', onFocusIn);
    window.addEventListener('hashchange', onHashChange);

    if (!map || typeof window.matchMedia !== 'function' || typeof window.IntersectionObserver !== 'function') {
      finishMotion();
    } else {
      try {
        motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        if (typeof motionQuery.addEventListener === 'function') {
          motionQuery.addEventListener('change', onMotionChange);
        } else if (typeof motionQuery.addListener === 'function') {
          motionQuery.addListener(onMotionChange);
        } else {
          finishMotion();
        }

        if (motionQuery.matches) {
          finishMotion();
        } else if (!motionFinished) {
          root.dataset.buildingState = 'core';
          root.dataset.buildingMotion = 'pending';
          observer = new window.IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.target === map && entry.isIntersecting && entry.intersectionRatio >= 0.2)) {
              startMotion();
            }
          }, { threshold: 0.2 });
          observer.observe(map);
        }
      } catch {
        finishMotion();
      }
    }

    return () => {
      disposed = true;
      clearMotion();
      root.removeEventListener('click', onClick);
      root.removeEventListener('keydown', onKeyDown);
      root.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('hashchange', onHashChange);

      if (motionQuery) {
        if (typeof motionQuery.removeEventListener === 'function') {
          motionQuery.removeEventListener('change', onMotionChange);
        } else if (typeof motionQuery.removeListener === 'function') {
          motionQuery.removeListener(onMotionChange);
        }
      }

      rootAttributes.forEach(([name, value]) => restoreAttribute(root, name, value));
      topicAttributes.forEach(([topic, selected, tabIndex]) => {
        restoreAttribute(topic, 'aria-selected', selected);
        restoreAttribute(topic, 'tabindex', tabIndex);
      });
      panelVisibility.forEach(([panel, hidden]) => {
        panel.hidden = hidden;
      });
    };
  };

  const init = (scope) => {
    const roots = Array.from(scope.querySelectorAll('.bf-building'));

    if (typeof scope.matches === 'function' && scope.matches('.bf-building')) {
      roots.unshift(scope);
    }

    roots.forEach((root) => {
      if (!instances.has(root)) {
        const destroy = createInstance(root);

        if (destroy) {
          instances.set(root, destroy);
        }
      }
    });
  };

  const destroy = (scope) => {
    instances.forEach((dispose, root) => {
      if (scope === root || scope.contains(root)) {
        dispose();
        instances.delete(root);
      }
    });
  };

  window[controllerKey] = { init, destroy };
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
